import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stage } from '@react-three/drei';
import { useDesignStore } from '../../store/designStore';
import { ModelErrorBoundary, TexturedModel, useDesignOverlayTexture } from './Model3DScene';
import { VariantSelector } from './VariantSelector';
import { ComingSoonOverlay } from './ComingSoonOverlay';

interface Model3DPreviewMiniProps {
  onExpand: () => void;
}

// How long the attention-pulse rings keep animating around the closed
// thumbnail badge after first load, before settling to a plain static icon —
// just long enough to catch the eye once, not forever.
const ATTENTION_PULSE_MS = 6000;

// Floating "min version" of the 3D Preview — lives on top of the Design tab,
// bottom-right, chat-widget style, so the customer can see how their design
// wraps onto the real product without leaving the 2D editor. Per product
// request, the live 3D canvas (WebGL + GLTF load + texture bake) is no
// longer mounted automatically — the widget starts as a small static
// thumbnail badge, and only the customer clicking it mounts the actual
// `<Canvas>`. The expand icon (once open) hands off to the same full
// `Model3DPreview` tab (`onExpand`, wired by the caller to
// `setActiveTab('3d')`) rather than duplicating its zoom/rotate controls
// here — this widget only needs to be a live glance.
export function Model3DPreviewMini({ onExpand }: Model3DPreviewMiniProps) {
  const { t } = useTranslation();
  const { productConfig, variants, selectedVariantOptions } = useDesignStore();
  const textureUrl = useDesignOverlayTexture();
  const [isOpen, setIsOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showAttention, setShowAttention] = useState(true);
  const has3dModel = !!productConfig?.glbModelUrl;

  useEffect(() => {
    const timer = setTimeout(() => setShowAttention(false), ATTENTION_PULSE_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!isOpen) {
    return (
      <button
        onClick={() => { setIsOpen(true); setShowAttention(false); }}
        title={t('designer.preview3d.mini.reopen')}
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 500,
          width: 52, height: 52, borderRadius: '50%', border: 'none',
          background: 'var(--primary, #222222)', color: '#fff', fontSize: 16, fontWeight: 800,
          cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {showAttention && (
          <>
            <span style={pulseRingStyle(0)} />
            <span style={pulseRingStyle(0.6)} />
          </>
        )}
        3D
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 20, right: 20, zIndex: 500,
      width: 358, maxHeight: '70vh', display: 'flex', flexDirection: 'column',
      background: '#ffffff', borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 8px 24px rgba(15,23,42,0.18)', border: '1px solid #e2e8f0',
    }}>
      {/* Header — chat-window convention: title left, action icons right */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
          {t('designer.preview3d.mini.title')}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {has3dModel && !failed && (
            <button onClick={onExpand} title={t('designer.preview3d.mini.expand')} style={iconBtn}>⤢</button>
          )}
          <button onClick={() => setIsOpen(false)} title={t('designer.preview3d.mini.close')} style={iconBtn}>×</button>
        </div>
      </div>

      {!has3dModel ? (
        <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          {t('designer.preview3d.unavailable')}
        </div>
      ) : failed ? (
        <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          {t('designer.preview3d.loadFailed')}
        </div>
      ) : (
        <>
          <div style={{ height: 360, background: '#f0f0f0', position: 'relative' }}>
            <Canvas camera={{ position: [0, 0, 3], fov: 45 }}>
              <Suspense fallback={null}>
                <ModelErrorBoundary onError={() => setFailed(true)}>
                  <Stage environment="city" intensity={0.5} adjustCamera>
                    <TexturedModel
                      url={productConfig!.glbModelUrl!}
                      designOverlayUrl={textureUrl}
                      variants={variants}
                      selectedVariantOptions={selectedVariantOptions}
                    />
                  </Stage>
                </ModelErrorBoundary>
              </Suspense>
              <OrbitControls autoRotate enableZoom enablePan={false} makeDefault />
            </Canvas>

            <ComingSoonOverlay />

            {/* Floats the Variant picker over the live model itself (glassy,
                translucent) instead of stacking it below — keeps the whole
                widget no taller than header + canvas. */}
            <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12 }}>
              <VariantSelector layout="inline" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  width: 24, height: 24, borderRadius: 6, border: 'none', background: 'transparent',
  color: '#475569', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

if (typeof document !== 'undefined' && !document.getElementById('model3d-mini-pulse-keyframes')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'model3d-mini-pulse-keyframes';
  styleEl.textContent = `
    @keyframes model3d-mini-pulse {
      0% { transform: scale(1); opacity: 0.55; }
      100% { transform: scale(1.9); opacity: 0; }
    }
  `;
  document.head.appendChild(styleEl);
}

// Two staggered expanding rings (offset via `delay`) behind the closed
// thumbnail badge — a brief, one-time visual nudge so a first-time customer
// notices the 3D preview is clickable, since the round "3D" badge alone
// reads as decorative rather than interactive.
function pulseRingStyle(delay: number): React.CSSProperties {
  return {
    position: 'absolute', inset: 0, borderRadius: '50%',
    border: '2px solid var(--primary, #222222)',
    animation: `model3d-mini-pulse 1.8s ease-out ${delay}s infinite`,
    pointerEvents: 'none',
  };
}
